// @flow
import * as React from 'react';
import {Box, Typography, IconButton} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {
  HelpOutline,
  Storefront,
  PeopleAlt,
  RecordVoiceOver,
  VerifiedUser,
} from '@material-ui/icons';

import CustomModal from '../../../../elements/CustomModal/CustomModal';
import TiendadaTooltip from '../../../../elements/tiendada-tooltip';
import {colors} from '../../../../assets/styles/colors';

const useStyles = makeStyles(theme => ({
  modalContainer: {
    width: '100%',
    maxWidth: 560,
  },
  hero: {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.purple3} 100%)`,
    borderRadius: '16px',
    padding: '1.25rem',
    color: 'white',
    marginBottom: '1.25rem',
    boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.08)',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '.35rem',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '999px',
    padding: '.15rem .75rem',
    fontSize: '.75rem',
    fontWeight: 600,
    marginBottom: '.5rem',
  },
  heroTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    marginBottom: '.35rem',
  },
  heroSubtitle: {
    fontSize: '.95rem',
    lineHeight: 1.4,
    opacity: 0.95,
  },
  tipsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '1rem',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  tipCard: {
    borderRadius: '14px',
    border: `1px solid ${colors.grey200}`,
    padding: '1rem',
    backgroundColor: colors.grey050,
    boxShadow: '0 5px 15px rgba(15, 23, 42, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  tipHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '.75rem',
  },
  tipIcon: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.primary,
    boxShadow: '0 4px 10px rgba(15, 23, 42, 0.08)',
  },
  tipTitle: {
    fontSize: '.95rem',
    fontWeight: 700,
    color: colors.grey900,
  },
  tipDescription: {
    fontSize: '.85rem',
    color: colors.grey700,
    lineHeight: 1.35,
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.4rem',
  },
  chip: {
    backgroundColor: 'white',
    borderRadius: '999px',
    padding: '.2rem .65rem',
    fontSize: '.75rem',
    fontWeight: 600,
    color: colors.grey700,
    border: `1px solid ${colors.grey200}`,
  },
  footerNote: {
    marginTop: '1.25rem',
    padding: '.85rem 1rem',
    borderRadius: '12px',
    backgroundColor: colors.grey100,
    fontSize: '.8rem',
    color: colors.grey700,
    lineHeight: 1.4,
  },
}));

type Tip = {
  id: string,
  title: string,
  description: string,
  chips: Array<string>,
  icon: React.Node,
};

const TIPS: Array<Tip> = [
  {
    id: 'business',
    title: '¿Qué vendes?',
    description: 'Resume en 1-2 líneas tu producto o servicio estrella.',
    chips: ['Pines personalizados', 'Flores mágicas', 'Servicios únicos'],
    icon: <Storefront fontSize="small" />,
  },
  {
    id: 'customers',
    title: '¿Para quién es?',
    description: 'Describe a tu cliente ideal, ciudad o momento de compra.',
    chips: ['Regalos rápidos', 'Eventos corporativos', 'Clientes B2B'],
    icon: <PeopleAlt fontSize="small" />,
  },
  {
    id: 'tone',
    title: 'Tono del bot',
    description: 'Elige cómo debe sonar: cercano, premium, divertido, etc.',
    chips: ['Cercano y claro', 'Premium', 'Amigable'],
    icon: <RecordVoiceOver fontSize="small" />,
  },
  {
    id: 'rules',
    title: 'Reglas clave',
    description: 'Aclara límites, horarios, políticas o pasos obligatorios.',
    chips: ['Sin prometer stock', 'Horario 9am-7pm', 'Política de cambios'],
    icon: <VerifiedUser fontSize="small" />,
  },
];

type PromptHelpModalProps = {
  open: boolean,
  onClose: () => void,
};

export function PromptHelpModal({open, onClose}: PromptHelpModalProps) {
  const classes = useStyles();

  return (
    <CustomModal open={open} handleCloseModal={onClose}>
      <Box
        className={classes.modalContainer}
        display="flex"
        flexDirection="column"
        p={2}
      >
        <div className={classes.hero}>
          <div className={classes.heroBadge}>
            <span role="img" aria-label="Guía rápida">
              ⚡
            </span>
            <span>Mini guía express</span>
          </div>
          <Typography className={classes.heroTitle}>
            Haz que tu bot suene como tu negocio
          </Typography>
          <Typography className={classes.heroSubtitle}>
            Completa estas 4 tarjetas rápidas y tendrás un prompt claro, útil y
            listo para guardar.
          </Typography>
        </div>

        <div className={classes.tipsGrid}>
          {TIPS.map(tip => (
            <div key={tip.id} className={classes.tipCard}>
              <div className={classes.tipHeader}>
                <div className={classes.tipIcon}>{tip.icon}</div>
                <Typography className={classes.tipTitle}>
                  {tip.title}
                </Typography>
              </div>
              <Typography className={classes.tipDescription}>
                {tip.description}
              </Typography>
              <div className={classes.chipList}>
                {tip.chips.map(chip => (
                  <span key={`${tip.id}-${chip}`} className={classes.chip}>
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Typography className={classes.footerNote}>
          ProTip: escribe frases cortas, separadas por guiones o viñetas.
          Mientras más contexto concreto le das al bot (productos raros,
          servicios únicos, forma en la que hablas), más naturales serán sus
          respuestas.
        </Typography>
      </Box>
    </CustomModal>
  );
}

type PromptHelpIconButtonProps = {
  onClick: () => void,
};

export function PromptHelpIconButton({onClick}: PromptHelpIconButtonProps) {
  return (
    <TiendadaTooltip title="Ver guía para crear el prompt">
      <IconButton
        size="small"
        onClick={onClick}
        aria-label="Ver guía de prompt"
      >
        <HelpOutline fontSize="small" />
      </IconButton>
    </TiendadaTooltip>
  );
}
